import { Particles } from '@/components/ui/particles';
import { AnimatedShinyText } from '@/components/ui/animated-shiny-text';
import { AnimatedGradientText } from '@/components/ui/animated-gradient-text';
import { PulsatingButton } from '@/components/ui/pulsating-button';
import { BlurFade } from '@/components/ui/blur-fade';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/hooks/useTheme';
import { ArrowRight, Lock } from 'lucide-react';

export function HeroSection() {
  const { actualTheme } = useTheme();
  const particleColor = actualTheme === 'dark' ? '#60a5fa' : '#1d4ed8';

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
      <Particles
        className="absolute inset-0"
        quantity={50}
        color={particleColor}
        size={0.6}
        staticity={30}
        ease={80}
      />

      <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-20 text-center">
        <BlurFade delay={0} inView>
          <div className="mb-6 flex justify-center">
            <div className="inline-flex items-center rounded-full border border-border bg-background/50 backdrop-blur-sm px-4 py-1.5">
              <AnimatedShinyText className="text-sm">
                Enterprise-Grade Helpdesk Platform
              </AnimatedShinyText>
            </div>
          </div>
        </BlurFade>

        <BlurFade delay={0.1} inView>
          <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-foreground">
            Customer Support,{' '}
            <AnimatedGradientText
              colorFrom="#1d4ed8"
              colorTo="#60a5fa"
              className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold"
            >
              Reimagined
            </AnimatedGradientText>
          </h1>
        </BlurFade>

        <BlurFade delay={0.2} inView>
          <p className="mt-4 sm:mt-6 text-sm sm:text-base md:text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Streamline your support operations with intelligent automation, real-time
            collaboration, and complete multi-tenant isolation. Built for teams that
            take customer service seriously.
          </p>
        </BlurFade>

        <BlurFade delay={0.3} inView>
          <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <PulsatingButton
              pulseColor="hsl(217, 91%, 35%)"
              className="text-base px-6 py-3"
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Get in Touch
              <ArrowRight className="ml-2 h-4 w-4 inline" />
            </PulsatingButton>
            <Button
              variant="outline"
              size="lg"
              className="text-base"
              onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
            >
              See Features
            </Button>
          </div>
        </BlurFade>

        <BlurFade delay={0.5} inView>
          <div className="mt-10 sm:mt-16 max-w-4xl mx-auto rounded-lg sm:rounded-xl overflow-hidden shadow-2xl border border-border">
            {/* Browser chrome */}
            <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 bg-card border-b border-border">
              {/* Traffic lights */}
              <div className="flex items-center gap-1 sm:gap-1.5">
                <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-[#FF5F57]" />
                <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-[#FEBC2E]" />
                <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-[#28C840]" />
              </div>
              {/* Address bar */}
              <div className="flex-1 flex justify-center min-w-0">
                <div className="flex items-center gap-1 sm:gap-1.5 bg-muted rounded-md px-2 sm:px-3 py-1 w-full max-w-sm justify-center">
                  <Lock className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-muted-foreground shrink-0" />
                  <span className="text-[10px] sm:text-xs text-muted-foreground truncate">
                    euserviceline.com/helpdesk/dashboard
                  </span>
                </div>
              </div>
              {/* Spacer to balance traffic lights — hidden on mobile */}
              <div className="hidden sm:block w-[54px]" />
            </div>
            {/* Screenshot */}
            <img
              src={actualTheme === 'dark' ? '/screenshots/DashboardDark.png' : '/screenshots/DashboardLight.png'}
              alt="EU ServiceLine Dashboard"
              className="w-full h-auto block"
            />
          </div>
        </BlurFade>
      </div>
    </section>
  );
}
